import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { Button } from 'components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { OtpInput } from 'components/ui/otp-input';
import { AuthLayout, AuthCard } from 'components/auth';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'modules/auth/context';
import { useVerifyEmail, useResendEmailVerification } from 'modules/auth/hooks';

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    state: { user },
    methods: { logout },
  } = useAuthContext();

  const verifyMutation = useVerifyEmail();
  const resendMutation = useResendEmailVerification();

  const [code, setCode] = React.useState('');
  const [cooldown, setCooldown] = React.useState(0);

  // Already verified → out you go.
  React.useEffect(() => {
    if (user?.isEmailVerified) {
      navigate('/app', { replace: true });
    }
  }, [user?.isEmailVerified, navigate]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleSubmit = async (codeValue: string) => {
    // Guard against double-fire: OtpInput's onComplete races with button onClick on paste.
    if (verifyMutation.isPending) return;
    try {
      await verifyMutation.mutateAsync({ code: codeValue });
      toast.success(t('success'), { description: t('auth.email_verified') });
      navigate('/app', { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('error'), {
        description: err?.response?.data?.message || t('auth.verification_failed'),
      });
      setCode('');
    }
  };

  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync();
      setCooldown(60);
      toast.success(t('auth.code_sent_title'), { description: t('auth.code_sent_desc') });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('error'), {
        description: err?.response?.data?.message || t('auth.could_not_resend'),
      });
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <CardHeader className="space-y-1">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground">
            <MailCheck size={24} aria-hidden />
          </div>
          <CardTitle className="text-2xl font-bold">{t('auth.verify_email_title')}</CardTitle>
          <CardDescription>{t('auth.verify_email_desc', { email: user?.email ?? '' })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <OtpInput
              value={code}
              onChange={setCode}
              onComplete={handleSubmit}
              disabled={verifyMutation.isPending}
              autoFocus
              ariaInvalid={verifyMutation.isError}
            />
          </div>

          <Button
            type="button"
            className="w-full"
            size="lg"
            disabled={verifyMutation.isPending || code.length !== 6}
            onClick={() => handleSubmit(code)}
          >
            {verifyMutation.isPending ? t('auth.verifying') : t('auth.verify_email_button')}
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            {cooldown > 0 ? (
              <span>{t('auth.resend_in_seconds', { seconds: cooldown })}</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendMutation.isPending}
                className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2 disabled:opacity-50"
              >
                {t('auth.didnt_receive_resend')}
              </button>
            )}
          </div>

          <div className="border-t border-border/60 pt-4 text-center">
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('auth.wrong_email_signout')}
            </button>
          </div>
        </CardContent>
      </AuthCard>
    </AuthLayout>
  );
};

export default VerifyEmail;
